/**
 * Mockup Layers Utilities
 *
 * Shared utilities for mockup layer handling.
 * Used by both CanvasEditor (interactive) and MockupRenderer (static).
 */

import type { MockupBlendMode } from '@/api/products';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Fallback heather pattern URL (from Shirtigo reference).
 * Used when a color is heathered but no specific pattern is defined.
 */
// Bundled app asset (apps/web/public). Self-hosting keeps the melange
// effect independent of external domains and avoids canvas tainting on
// export — the previous shirtigo.de URL is dead (redirects to prinovia.com).
export const HEATHER_PATTERN_FALLBACK = '/heather_pattern.png';

/**
 * Default overlay settings
 */
export const DEFAULT_OVERLAY_SETTINGS = {
  heatheredOpacity: 0.5,
  heatheredBlendMode: 'OVERLAY' as MockupBlendMode,
  textureOpacity: 0.4,
  textureBlendMode: 'MULTIPLY' as MockupBlendMode,
  shadowOpacity: 0.3,
  shadowBlendMode: 'MULTIPLY' as MockupBlendMode,
} as const;

// =============================================================================
// BLEND MODE MAPPING
// =============================================================================

/**
 * Map API blend modes to CSS mix-blend-mode values.
 * Used for HTML overlay elements.
 */
export function mapBlendMode(blendMode: MockupBlendMode | undefined): string {
  if (!blendMode) return 'normal';

  const blendMap: Record<MockupBlendMode, string> = {
    NORMAL: 'normal',
    MULTIPLY: 'multiply',
    SCREEN: 'screen',
    OVERLAY: 'overlay',
    SOFT_LIGHT: 'soft-light',
    HARD_LIGHT: 'hard-light',
  };

  return blendMap[blendMode] || 'normal';
}

/**
 * Map API blend modes to Canvas 2D globalCompositeOperation values.
 * Used for canvas-based export with overlays.
 */
export function mapBlendModeToCanvas(
  blendMode: MockupBlendMode | undefined
): GlobalCompositeOperation {
  if (!blendMode) return 'source-over';

  const blendMap: Record<MockupBlendMode, GlobalCompositeOperation> = {
    NORMAL: 'source-over',
    MULTIPLY: 'multiply',
    SCREEN: 'screen',
    OVERLAY: 'overlay',
    SOFT_LIGHT: 'soft-light',
    HARD_LIGHT: 'hard-light',
  };

  return blendMap[blendMode] || 'source-over';
}

// =============================================================================
// IMAGE LOADING
// =============================================================================

/**
 * Load an image from URL and return as HTMLImageElement.
 * Used for canvas-based export operations.
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));

    img.src = url;
  });
}

/**
 * Get heathered overlay URL with fallback.
 */
export function getHeatheredOverlayUrl(
  heatheredOverlayUrl: string | null | undefined,
  isHeathered: boolean
): string | null {
  if (!isHeathered) return null;
  return heatheredOverlayUrl || HEATHER_PATTERN_FALLBACK;
}
